import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { DISPOSITIONS, DISPOSITION_LABELS, PAYER_TYPE_LABELS, REIMBURSEMENT_METHODS, apiError, formatAmount, formatBalance, formatDay, formatTotals, reportParams, toMinor } from "../../lib/finance";
import { DispositionBadge, WorkflowBadge } from "./FinanceBadges";
import DecisionDialog from "./DecisionDialog";
import ReimbursementDialog, { CancelReimbursementDialog } from "./ReimbursementDialog";
import { downloadExport } from "./ExportDialog";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Skeleton } from "../ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { Download, Info, RefreshCw } from "lucide-react";

const EMPTY = { dateFrom: "", dateTo: "", preconstitution: false };

// What is still due to a payer, per currency (only currencies with something left).
const dueOf = (balance) => Object.entries(balance || {}).filter(([c, b]) => (toMinor(b.remaining, c) || 0) > 0);

// « Contributions »: what each person (or Jamora, or a third party) actually paid, per currency, never converted and
// never presented as a debt; reimbursement is a separate, later decision.
export default function ContributionsPanel({ canExport, canDecide }) {
  const [filters, setFilters] = useState(EMPTY);
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  const [open, setOpen] = useState(null); // payer key
  const [detail, setDetail] = useState({ status: "idle", data: null, error: "" });
  const [reloadDetail, setReloadDetail] = useState(0);
  const [selected, setSelected] = useState([]); // funding ids of the payer sheet
  const [show, setShow] = useState("all"); // disposition filter of the payer sheet
  const [deciding, setDeciding] = useState(false);
  const [reimb, setReimb] = useState({ status: "idle", data: null });
  const [reimbursing, setReimbursing] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const invalid = filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo;

  const load = useCallback(async () => {
    if (invalid) return;
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const { data } = await api.get("/finance/contributions", { params: reportParams(filters) });
      setState({ status: "ready", data, error: "" });
    } catch (e) {
      setState({ status: "error", data: null, error: apiError(e) });
    }
  }, [filters, invalid]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { setSelected([]); setShow("all"); }, [open]);

  useEffect(() => {
    if (!open) return;
    setDetail((d) => ({ status: "loading", data: reloadDetail ? d.data : null, error: "" }));
    api.get("/finance/contributions", { params: { ...reportParams(filters), payer: open } })
      .then(({ data }) => setDetail({ status: "ready", data, error: "" }))
      .catch((e) => setDetail({ status: "error", data: null, error: apiError(e) }));
  }, [open, filters, reloadDetail]);

  useEffect(() => {
    if (!open) return;
    setReimb((r) => ({ status: "loading", data: reloadDetail ? r.data : null }));
    api.get("/finance/reimbursements", { params: { payer: open } })
      .then(({ data }) => setReimb({ status: "ready", data }))
      .catch(() => setReimb({ status: "error", data: null }));
  }, [open, reloadDetail]);

  const payers = state.data?.payers || [];
  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const items = (detail.data?.items || []).filter((i) => show === "all" || i.disposition === show);
  const decidable = items.filter((i) => i.decidable);
  const allSelected = decidable.length > 0 && decidable.every((i) => selected.includes(i.funding_id));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected(allSelected ? [] : decidable.map((i) => i.funding_id));
  const chosen = (detail.data?.items || []).filter((i) => i.decidable && selected.includes(i.funding_id));
  const byDisposition = detail.data?.payer?.by_disposition || {};
  const recorded = () => { setSelected([]); setReloadDetail((n) => n + 1); load(); };
  const balances = reimb.data?.balances || {};
  const due = dueOf(balances);
  const reimbursements = reimb.data?.items || [];

  return (
    <section className="space-y-4" aria-label="Contributions" data-testid="finance-contributions">
      <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
        <Info size={16} className="text-jamora shrink-0 mt-0.5" />
        <span>Ce que chacun a payé pour Jamora. Ce n'est pas une dette : un éventuel remboursement sera décidé séparément. Les montants restent dans leur devise, sans conversion.</span>
      </div>

      <Card className="p-4 flex flex-col md:flex-row md:items-end gap-3">
        <div className="grid grid-cols-2 gap-3 md:w-[340px]">
          <div className="space-y-1.5">
            <Label htmlFor="contrib-from">Payé du</Label>
            <Input id="contrib-from" type="date" value={filters.dateFrom} onChange={(e) => set("dateFrom", e.target.value)} className="text-base md:text-sm" data-testid="finance-contrib-from" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contrib-to">au</Label>
            <Input id="contrib-to" type="date" value={filters.dateTo} onChange={(e) => set("dateTo", e.target.value)} className="text-base md:text-sm" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm md:pb-2.5">
          <Checkbox checked={filters.preconstitution} onCheckedChange={(v) => set("preconstitution", v === true)} data-testid="finance-contrib-pre" />
          Dépenses avant constitution seulement
        </label>
        <div className="flex gap-2 md:ml-auto">
          {(filters.dateFrom || filters.dateTo || filters.preconstitution) && <Button variant="ghost" onClick={() => setFilters(EMPTY)}>Réinitialiser</Button>}
          {canExport && (
            <Button variant="outline" disabled={invalid} onClick={() => downloadExport("contributions", filters)} data-testid="finance-contrib-export">
              <Download size={15} className="mr-1.5" /> Exporter
            </Button>
          )}
        </div>
      </Card>
      {invalid && <p role="alert" className="text-sm text-red-700">La date de début est après la date de fin.</p>}

      {state.status === "loading" && !state.data ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : state.status === "error" ? (
        <Card className="p-5 text-center space-y-2">
          <p className="text-sm text-slate-600">{state.error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Réessayer</Button>
        </Card>
      ) : !payers.length ? (
        <Card className="p-6 text-center space-y-1">
          <h3 className="font-display font-semibold text-slate-900">Aucun paiement sur cette période</h3>
          <p className="text-sm text-slate-500">Les paiements enregistrés dans « Qui a payé ? » apparaîtront ici, regroupés par payeur.</p>
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {formatTotals(state.data.totals).map((t) => (
              <Card key={t} className="p-4"><div className="text-xs font-medium text-slate-500">Total payé</div><div className="text-xl font-semibold text-slate-900 tabular-nums mt-1">{t}</div></Card>
            ))}
          </div>
          <Card className="overflow-hidden">
            <table className="hidden md:table w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
                <tr><th className="px-3 py-2.5">Payeur</th><th className="px-3 py-2.5">Paiements</th><th className="px-3 py-2.5">Dernier paiement</th><th className="px-3 py-2.5 text-right">Reste dû</th><th className="px-3 py-2.5 text-right">Total payé</th></tr>
              </thead>
              <tbody>
                {payers.map((p) => (
                  <tr key={p.key} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(p.key)} data-testid="finance-contrib-row">
                    <td className="px-3 py-2.5">
                      <button type="button" className="font-medium text-slate-900 hover:underline text-left" onClick={(e) => { e.stopPropagation(); setOpen(p.key); }}>{p.label}</button>
                      <div className="text-xs text-slate-500">{PAYER_TYPE_LABELS[p.payer_type]}</div>
                    </td>
                    <td className="px-3 py-2.5 tabular-nums">{p.count}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{p.last_paid_at ? formatDay(p.last_paid_at) : "Aucun"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-amber-800">{dueOf(p.balance).map(([c, b]) => <div key={c}>{formatBalance(b.remaining, c)}</div>)}{!dueOf(p.balance).length && <span className="text-slate-400">Rien</span>}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{formatTotals(p.totals).map((t) => <div key={t}>{t}</div>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="md:hidden divide-y divide-slate-100">
              {payers.map((p) => (
                <button key={p.key} type="button" onClick={() => setOpen(p.key)} className="w-full text-left p-3 flex justify-between gap-3 hover:bg-slate-50" data-testid="finance-contrib-card">
                  <span className="min-w-0">
                    <span className="block font-medium text-slate-900 break-words">{p.label}</span>
                    <span className="block text-xs text-slate-500">{PAYER_TYPE_LABELS[p.payer_type]} · {p.count} paiement{p.count > 1 ? "s" : ""}</span>
                    {dueOf(p.balance).length > 0 && (
                      <span className="block text-xs text-amber-800 tabular-nums">Reste dû : {dueOf(p.balance).map(([c, b]) => formatBalance(b.remaining, c)).join(" · ")}</span>
                    )}
                  </span>
                  <span className="text-right tabular-nums font-semibold whitespace-nowrap">{formatTotals(p.totals).map((t) => <span key={t} className="block">{t}</span>)}</span>
                </button>
              ))}
            </div>
          </Card>
        </>
      )}

      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="finance-contrib-sheet">
          <SheetHeader>
            <SheetTitle>{detail.data?.payer?.label || payers.find((p) => p.key === open)?.label || "Payeur"}</SheetTitle>
            <SheetDescription>Paiements enregistrés{filters.dateFrom || filters.dateTo ? " sur la période choisie" : ""}. Ce total ne vaut pas créance.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            {detail.status === "loading" && !detail.data && <Skeleton className="h-32 w-full" />}
            {detail.status === "error" && <p className="text-sm text-slate-600">{detail.error}</p>}
            {detail.data && (
              <>
                <div className="flex flex-wrap gap-2">{formatTotals(detail.data.payer.totals).map((t) => <span key={t} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold tabular-nums">{t}</span>)}</div>
                {Object.keys(byDisposition).length > 0 && (
                  <div className="rounded-lg border border-slate-200 p-3 space-y-1.5" data-testid="finance-contrib-dispositions">
                    <div className="text-xs font-semibold text-slate-500">Avances par décision</div>
                    {DISPOSITIONS.filter((d) => byDisposition[d]).map((d) => (
                      <div key={d} className="flex justify-between gap-3 text-sm">
                        <DispositionBadge status={d} />
                        <span className="tabular-nums text-right">{formatTotals(byDisposition[d]).join(" · ")}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(Object.keys(balances).length > 0 || reimbursements.length > 0) && (
                  <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="finance-reimbursements">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-slate-500">Remboursements</span>
                      {canDecide && due.length > 0 && (
                        <Button size="sm" variant="outline" onClick={() => setReimbursing(true)} data-testid="finance-reimbursement-open">Enregistrer un remboursement</Button>
                      )}
                    </div>
                    {Object.entries(balances).map(([c, b]) => (
                      <div key={c} className="grid grid-cols-3 gap-2 text-sm" data-testid="finance-reimbursement-balance">
                        <span><span className="block text-xs text-slate-500">À rembourser</span><span className="tabular-nums">{formatBalance(b.owed, c)}</span></span>
                        <span><span className="block text-xs text-slate-500">Remboursé</span><span className="tabular-nums">{formatBalance(b.reimbursed, c)}</span></span>
                        <span><span className="block text-xs text-slate-500">Reste dû</span><strong className="tabular-nums">{formatBalance(b.remaining, c)}</strong></span>
                        {b.advanced && Number(b.advanced) > 0 && (
                          <span className="col-span-3 text-xs text-slate-500" data-testid="finance-reimbursement-advanced">
                            Avance de mission déjà remise : {formatBalance(b.advanced, c)}, déduite du reste dû{Number(b.to_return) > 0 ? ` ; ${formatBalance(b.to_return, c)} à restituer` : ""}.
                          </span>
                        )}
                      </div>
                    ))}
                    {reimbursements.length > 0 && (
                      <ul className="divide-y divide-slate-100 border-t border-slate-100 pt-1">
                        {reimbursements.map((r) => (
                          <li key={r.id} className={`py-2 flex gap-3 text-sm ${r.cancelled_at ? "text-slate-500" : ""}`} data-testid="finance-reimbursement-item">
                            <span className="min-w-0 flex-1">
                              <span className="flex flex-wrap items-center gap-1.5">
                                <span className={r.cancelled_at ? "line-through" : "text-slate-900"}>{formatDay(r.paid_on)} · {REIMBURSEMENT_METHODS[r.method] || r.method}</span>
                                {r.cancelled_at && <span className="rounded-full border border-slate-300 px-1.5 text-2xs font-medium text-slate-600">Annulé</span>}
                              </span>
                              {r.reference && <span className="block text-xs text-slate-500 break-words">{r.reference}</span>}
                              {r.cancelled_at && <span className="block text-xs break-words">Motif : {r.cancel_reason}</span>}
                            </span>
                            <span className="flex items-start gap-1 shrink-0">
                              <span className={`tabular-nums whitespace-nowrap ${r.cancelled_at ? "line-through text-slate-400" : "font-medium"}`}>{formatAmount(r.amount, r.currency)}</span>
                              {canDecide && !r.cancelled_at && (
                                <Button type="button" variant="ghost" size="sm" className="h-auto py-0 text-slate-500 hover:text-red-700" onClick={() => setCancelling(r)}
                                  aria-label={`Annuler le remboursement du ${formatDay(r.paid_on)}`} data-testid="finance-reimbursement-cancel">Annuler</Button>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {canDecide && Object.keys(byDisposition).length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-2 text-sm mr-auto">
                      <Checkbox checked={allSelected} disabled={!decidable.length} onCheckedChange={toggleAll} aria-label="Sélectionner toutes les avances affichées" data-testid="finance-contrib-select-all" />
                      Tout sélectionner
                    </label>
                    <select value={show} onChange={(e) => { setShow(e.target.value); setSelected([]); }} aria-label="Filtrer par décision"
                      className="h-9 rounded-md border border-slate-200 bg-white px-2 text-base md:text-sm" data-testid="finance-contrib-show">
                      <option value="all">Toutes les décisions</option>
                      {DISPOSITIONS.map((d) => <option key={d} value={d}>{DISPOSITION_LABELS[d]}</option>)}
                    </select>
                  </div>
                )}
                {items.length ? (
                  <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                    {items.map((i, index) => (
                      <li key={`${i.expense_id}-${i.funding_id || index}`} className="p-3 flex gap-3 text-sm" data-testid="finance-contrib-item">
                        {canDecide && i.decidable && (
                          <Checkbox checked={selected.includes(i.funding_id)} onCheckedChange={() => toggle(i.funding_id)} className="mt-0.5"
                            aria-label={`Sélectionner « ${i.title} »`} data-testid="finance-contrib-select" />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block text-slate-900 break-words">{i.title}</span>
                          <span className="flex flex-wrap items-center gap-1.5 mt-0.5 text-xs text-slate-500">
                            {i.paid_at ? `Payé le ${formatDay(i.paid_at)}` : "Date manquante"}{i.preconstitution && " · avant constitution"}
                            <WorkflowBadge status={i.status} />
                            <DispositionBadge status={i.disposition} />
                          </span>
                          {i.decided_on && (
                            <span className="block mt-0.5 text-xs text-slate-500 break-words">
                              Décidé le {formatDay(i.decided_on)}{i.decision_reference && ` · ${i.decision_reference}`}
                            </span>
                          )}
                        </span>
                        <strong className="tabular-nums whitespace-nowrap">{formatAmount(i.amount, i.currency)}</strong>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500">Aucun paiement avec cette décision.</p>
                )}
                {canDecide && chosen.length > 0 && (
                  <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-white border-t border-slate-200">
                    <Button className="w-full" onClick={() => setDeciding(true)} data-testid="finance-contrib-decide">
                      Consigner une décision ({chosen.length})
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
      {canDecide && (
        <ReimbursementDialog payer={open} payerLabel={detail.data?.payer?.label || ""} balances={balances} open={reimbursing}
          onOpenChange={setReimbursing} onRecorded={recorded} />
      )}
      {canDecide && <CancelReimbursementDialog reimbursement={cancelling} onClose={() => setCancelling(null)} onCancelled={recorded} />}
      {canDecide && (
        <DecisionDialog items={chosen} payerLabel={detail.data?.payer?.label || ""} open={deciding} onOpenChange={setDeciding} onRecorded={recorded} />
      )}
    </section>
  );
}
